import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export const ClientAiSummary = ({ client }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/ai-summary`).then((r) => setState(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { if (open && state === null) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (enabled) => {
    try {
      await api.patch(`/clients/${client.id}/ai-summary`, { enabled });
      setState((s) => ({ ...s, enabled }));
      toast.success(enabled ? "AI summaries turned on for this client" : "AI summaries turned off");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const generate = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${client.id}/ai-summary/generate${force ? "?force=true" : ""}`);
      setState((s) => ({ ...s, summary: r.data }));
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="w-full mt-2" data-testid={`ai-summary-${client.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-indigo-700 flex items-center gap-1" data-testid={`ai-summary-toggle-${client.id}`}>
        <Sparkles className="w-3.5 h-3.5" /> Weekly AI summary {open ? "▴" : "▾"}
      </button>
      {open && state && (
        <div className="mt-2 rounded-2xl bg-indigo-50/60 border border-indigo-100 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-indigo-900">One paragraph on practice patterns and the aggregate mood trend for the past 7 days. Opt-in per client; never shown to the client.</p>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Switch checked={state.enabled} onCheckedChange={toggle} data-testid={`ai-summary-switch-${client.id}`} /> {state.enabled ? "On" : "Off"}
            </label>
          </div>
          {state.enabled && (
            state.summary ? (
              <div className="bg-white rounded-xl px-4 py-3 border border-indigo-100 space-y-2" data-testid={`ai-summary-text-${client.id}`}>
                <p className="text-sm text-slate-800 leading-relaxed">{state.summary.text}</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-slate-400">Week of {fmt(state.summary.week_start)} · generated {fmt(state.summary.created_at)}</p>
                  <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => generate(true)} className="h-7 rounded-full text-xs text-indigo-700" data-testid={`ai-summary-refresh-${client.id}`}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" size="sm" disabled={busy} onClick={() => generate()} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`ai-summary-generate-${client.id}`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Summarising…" : "Generate this week's summary"}
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
};
